
import React, { useState, useEffect, useCallback } from 'react';
import CreatorsCard from '@/components/CreatorsCard/CreatorsCard';
import ResponsiveOverlay from '@/components/ResponsiveOverlay';
import { fetchProfile, getUserCountryCount } from '@/api/auth';
import { followCreator, unfollowCreator } from '@/api/creators';
import { useAuth } from '@/contexts';
import { useIsMobileViewport } from '@/hooks/useIsMobileViewport';
import { debug, error as logError } from '@/utils/logger';
import { toast } from 'react-toastify';
import User from '@/models/user';
import './CreatorCardModal.css';

// Initial data that can be passed from parent to avoid loading flash
export interface CreatorCardInitialData {
  name?: string;
  avatar?: string;
  city?: string;
  followersCount?: number;
  isFollowing?: boolean;
}

interface CreatorCardModalProps {
  isOpen: boolean;
  onClose: () => void;
  userId: string;
  initialData?: CreatorCardInitialData;
}

const CreatorCardModal: React.FC<CreatorCardModalProps> = ({
  isOpen,
  onClose,
  userId,
  initialData,
}) => {
  const isMobileViewport = useIsMobileViewport();
  const { user: currentUser } = useAuth();
  const [profile, setProfile] = useState<User | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [isFollowing, setIsFollowing] = useState<boolean>(initialData?.isFollowing || false);
  const [isFollowUpdating, setIsFollowUpdating] = useState<boolean>(false);
  const [countriesCount, setCountriesCount] = useState<number | undefined>(undefined);

  // Track if we have initial data to show immediately
  const hasInitialData = !!(initialData?.name || initialData?.avatar);

  const fetchUserProfile = useCallback(async () => {
    if (!isOpen || !userId) return;

    setLoading(true);
    setError(null);

    try {
      debug('[CreatorCardModal] Fetching profile', { userId });

      // Fetch profile and country count in parallel
      const [fetchedProfile, countryCount] = await Promise.all([
        fetchProfile(userId),
        currentUser ? getUserCountryCount(userId) : Promise.resolve(0),
      ]);

      setProfile(fetchedProfile);
      setCountriesCount(countryCount);

      // Set initial following state
      if (Array.isArray(currentUser?.following_user_ids)) {
        setIsFollowing(currentUser.following_user_ids.includes(userId));
      } else {
        setIsFollowing(fetchedProfile.is_following || false);
      }

      debug('[CreatorCardModal] Profile fetched successfully', {
        userId: fetchedProfile._id,
        userName: fetchedProfile.name,
        countriesCount: countryCount,
      });
    } catch (err) {
      logError('[CreatorCardModal] Error fetching profile', err);
      setError(err instanceof Error ? err.message : 'Failed to load profile');
    } finally {
      setLoading(false);
    }
  }, [isOpen, userId, currentUser]);

  useEffect(() => {
    if (isOpen) {
      void fetchUserProfile();
    } else {
      setProfile(null);
      setError(null);
      setCountriesCount(undefined);
    }
  }, [isOpen, fetchUserProfile]);

  const handleFollowClick = useCallback(async () => {
    if (!currentUser) {
      toast.error('Please sign in to follow users');
      return;
    }

    if (!profile) return;

    setIsFollowUpdating(true);

    // Optimistic update
    const wasFollowing = isFollowing;
    setIsFollowing(!wasFollowing);

    try {
      if (wasFollowing) {
        await unfollowCreator(userId);
        debug('[CreatorCardModal] Unfollowed user', { userId });
      } else {
        await followCreator(userId);
        debug('[CreatorCardModal] Followed user', { userId });
      }
    } catch (err) {
      // Revert on error
      setIsFollowing(wasFollowing);
      logError('[CreatorCardModal] Error toggling follow:', err);
      toast.error('Failed to update follow status');
    } finally {
      setIsFollowUpdating(false);
    }
  }, [currentUser, profile, isFollowing, userId]);

  if (!isOpen) return null;

  const isCurrentUser = currentUser?._id === userId;

  // Get hero image with fallbacks
  const heroImage = profile?.hero_image_url || profile?.heroImage || profile?.cover_image || '';
  const heroImageSecondary = profile?.hero_image_secondary_url || profile?.secondary_hero_image_url || '';

  // Determine what to show: full profile data or initialData with skeleton parts
  const showContent = profile || hasInitialData;
  const isLoadingFullData = loading && hasInitialData;

  const modalBody = (
    <>
      {loading && !hasInitialData && (
        <div className="creator-card-modal-skeleton">
          <div className="skeleton-hero" />
          <div className="skeleton-profile-content">
            <div className="skeleton-avatar-large" />
            <div className="skeleton-name-large" />
            <div className="skeleton-city-text" />
            <div className="skeleton-stats">
              <div className="skeleton-stat" />
              <div className="skeleton-stat" />
              <div className="skeleton-stat" />
            </div>
          </div>
        </div>
      )}

      {error && (
        <div className="creator-card-modal-error">
          <p>{error}</p>
          <button onClick={() => void fetchUserProfile()} className="creator-card-modal-retry">
            Retry
          </button>
        </div>
      )}

      {!error && showContent && (
        <div
          className={`creator-card-modal-content ${isLoadingFullData ? 'creator-card-modal-content--loading' : ''}`}
        >
          <CreatorsCard
            title={profile?.name || initialData?.name || 'Anonymous'}
            city={profile?.city_identifying_name || initialData?.city}
            followers={profile?.followers_count ?? initialData?.followersCount}
            countries={countriesCount}
            recs={profile?.recommendation_count || 0}
            authorAvatar={profile?.large_avatar_url || initialData?.avatar || ''}
            authorAvatarSecondary={profile?.small_avatar_url || ''}
            authorAvatarTertiary={profile?.avatar_url || ''}
            authorAvatarQuaternary={profile?.profile_image_url || ''}
            heroImage={heroImage}
            heroImageSecondary={heroImageSecondary}
            traveller_types={profile?.traveller_types?.map((t) => String(t)) || []}
            isFollowing={isFollowing}
            onFollowClick={profile && !isFollowUpdating ? handleFollowClick : undefined}
            id={profile?._id || userId}
            username={profile?.username}
            profile_type={profile?.profile_type}
            onCardClick={() => {}}
            isCurrentUser={isCurrentUser}
          />
        </div>
      )}
    </>
  );

  const stopPropagation = (event: React.MouseEvent) => {
    event.stopPropagation();
  };

  return (
    <ResponsiveOverlay
      onClose={onClose}
      presentation="sheet"
      forceMobileSheet={isMobileViewport}
      unwrapped={isMobileViewport}
      panelClassName={isMobileViewport ? undefined : 'creator-card-modal-dialog'}
      showSheetHandle={false}
      className={isMobileViewport ? 'creator-card-modal-overlay' : undefined}
      aria-label="Creator profile preview"
    >
      {isMobileViewport ? (
        <div className="creator-card-modal-drawer" onClick={stopPropagation}>
          {modalBody}
        </div>
      ) : (
        <div className="creator-card-modal-dialog-panel" onClick={stopPropagation}>
          {modalBody}
        </div>
      )}
    </ResponsiveOverlay>
  );
};

export default CreatorCardModal;
